import React from 'react'
import PropTypes from 'prop-types'
import {
    AmericanexpressIcon,
    MastercardIcon,
    CloseIcon,
    VisaIcon,
} from '../../../../components/icons'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Checkbox,
    Flex,
    Text,
    Button,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react'
import {remCalc} from '../../../../theme/_utils'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

export const brandIcons = {
    visa: <VisaIcon style={{width: remCalc(37)}} />,
    mc: <MastercardIcon style={{width: remCalc(37)}} />,
    americanExpress: <AmericanexpressIcon style={{width: remCalc(37)}} />,
}

const StoredCard = ({card, updateRender, updateCustomer, isDefault, handleItemRemoved = noop}) => {
    const styles = useMultiStyleConfig('Account')
    const {site} = useMultiSite()
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()

    var shopperReference = customerId
    const {data: customer} = useCurrentCustomer()

    if (customer?.authType === 'registered') {
        if (customer?.c_adyenToken) {
            shopperReference = customer.c_adyenToken
        }
    }

    const handleCheckboxChange = (e) => {
        const isChecked = e.target.checked

        updateCustomer
            .mutateAsync({
                body: {c_preferredPaymentToken: isChecked ? card.id : 'unset'},
                parameters: {customerId: customerId},
            })
            .catch((error) => {
                console.error('error', error)
            })
    }

    const removeCard = async () => {
        const token = await getTokenWhenReady()

        try {
            const response = await fetch(
                `/mobify/api/adyen/delete-stored-card?shopperReference=${shopperReference}&storedPaymentMethodId=${card.id}&siteId=${site.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                        customerid: customer.customerId,
                    },
                },
            )

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`)
            } else {
                updateRender()
                handleItemRemoved()
            }
        } catch (error) {
            console.error('Error deleting stored card:', error)
        }
    }

    const intl = useIntl()

    return (
        <Box {...styles.cardContainer} data-testid="stored-card" data-cs-mask>
            <Flex {...styles.brandContainer}>
                <Flex>
                    {brandIcons[card.brand.toLowerCase()]}
                    <Text {...styles.brandText}>
                        {card.name}{' '}
                        <FormattedMessage
                            id="payment_methods.card.endsWith"
                            defaultMessage="ends with"
                        />{' '}
                        {card.lastFour}
                    </Text>
                </Flex>
                <Button
                    onClick={() => removeCard()}
                    variant="ghost"
                    aria-label={intl.formatMessage(
                        {
                            id: 'label.payment_methods.card.remove',
                            defaultMessage: 'Remove card ending with {lastFour}',
                        },
                        {lastFour: card.lastFour},
                    )}
                    data-testid="close-icon"
                    minW={0}
                    p={2}
                    _focusVisible={{boxShadow: 'outline'}}
                >
                    <CloseIcon aria-hidden="true" focusable="false" />
                </Button>
            </Flex>
            <Box {...styles.infoContainer}>
                <Flex {...styles.infoNameContainer}>
                    <Text {...styles.infoLabel}>
                        <FormattedMessage
                            id="payment_methods.card.name"
                            defaultMessage="Name of the card"
                        />
                    </Text>
                    <Text {...styles.infoValue}>{card.holderName}</Text>
                </Flex>
                <Flex {...styles.infoExpirationContainer}>
                    <Text {...styles.infoLabel}>
                        <FormattedMessage
                            id="payment_methods.card.expDate"
                            defaultMessage="Expiration date"
                        />
                    </Text>
                    <Text {...styles.infoValue}>
                        {card.expiryMonth}/{card.expiryYear}
                    </Text>
                </Flex>
            </Box>

            <Flex {...styles.checkboxContainer}>
                <Checkbox isChecked={isDefault} onChange={handleCheckboxChange} />
                <Text {...styles.checkboxLabel}>
                    <FormattedMessage
                        id="payment_methods.card.preferredCheckbox"
                        defaultMessage="Set as default card"
                    />
                </Text>
            </Flex>
        </Box>
    )
}

export default StoredCard

StoredCard.propTypes = {
    card: PropTypes.shape({
        brand: PropTypes.string.isRequired,
        expiryMonth: PropTypes.string.isRequired,
        expiryYear: PropTypes.string.isRequired,
        holderName: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        lastFour: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    updateRender: PropTypes.func,
    updateCustomer: PropTypes.object,
    isDefault: PropTypes.bool,
    handleItemRemoved: PropTypes.func,
}
